import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useUserAuth } from '../context/UserAuthContext';
import { Sparkles, Home } from 'lucide-react';
import { LogoIcon } from '../components/Logo';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const HAS_WHITESPACE_REGEX = /\s/;

const UserSignup = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { signup } = useUserAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (HAS_WHITESPACE_REGEX.test(username) || HAS_WHITESPACE_REGEX.test(password)) {
      setError('Username and password cannot contain spaces');
      return;
    }

    if (!EMAIL_REGEX.test(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);

    const result = await signup(username, password, email);
    
    if (result.success) {
      navigate(from);
    } else {
      setError(result.error || 'Signup failed');
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#111111] flex relative">
      {/* Home Button - Top Right */}
      <Link 
        to="/" 
        className="absolute top-6 right-6 z-10 inline-flex items-center gap-2 px-4 py-2 text-white/50 hover:text-white border border-white/10 hover:border-white/20 transition-colors"
      >
        <Home className="h-4 w-4" />
        <span className="text-sm">Home</span>
      </Link>

      {/* Left Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-6 md:px-12 lg:px-20 py-12">
        <div className="max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-2 mb-12">
            <LogoIcon className="h-8 w-8" />
            {/* <span className="text-xl font-medium text-white">NeoPass</span> */}
          </div>

          {/* Title
          <h1 className="text-4xl md:text-5xl font-light text-white mb-4 tracking-tight">
            Create an account
          </h1> */}
          <p className="text-white/50 mb-4">
            Get started with NeoPass today
          </p>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-10 border border-accent/20 bg-accent/5 text-accent text-xs font-mono tracking-wide">
            <span>✦</span>
            <span>10 FREE CREDITS ON SIGNUP</span>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-6 p-4 border border-red-500/30 bg-red-500/10">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-white/40 text-xs font-mono tracking-widest mb-3">
                USERNAME
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value.replace(/\s/g, ''))}
                required
                disabled={isLoading}
                minLength={3}
                className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
                placeholder="Choose a username"
              />
              <p className="mt-2 text-xs text-white/40">No spaces allowed in username.</p>
            </div>

            <div>
              <label className="block text-white/40 text-xs font-mono tracking-widest mb-3">
                EMAIL
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label className="block text-white/40 text-xs font-mono tracking-widest mb-3">
                PASSWORD
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value.replace(/\s/g, ''))}
                required
                disabled={isLoading}
                minLength={6}
                className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
                placeholder="Create a password"
              />
              <p className="mt-2 text-xs text-white/40">No spaces allowed in password.</p>
            </div>

            <div>
              <label className="block text-white/40 text-xs font-mono tracking-widest mb-3">
                CONFIRM PASSWORD
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value.replace(/\s/g, ''))}
                required
                disabled={isLoading}
                className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
                placeholder="Confirm your password"
              />
            </div>

            <div className="p-4 border border-accent\/20 bg-accent\/5">
              <p className="text-xs text-accent\/80">
                ⚠️ Important: Password reset is not available. Please choose a password you will remember. By signing up, you agree to our <Link to="/must-read" className="underline hover:text-accent-light">Terms & Policies</Link> including our no-refund policy.
              </p>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full px-6 py-4 bg-white text-black font-medium hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

          <p className="mt-8 text-white/50 text-sm">
            Already have an account?{' '}
            <Link 
              to="/login" 
              state={{ from: location.state?.from }}
              className="text-white hover:text-accent transition-colors"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Gradient */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute -inset-y-[25%] -right-24 flex w-full flex-col blur md:-right-6"
          style={{
            maskImage: 'linear-gradient(to right, rgba(255, 255, 255, 0), rgb(255, 255, 255))',
            opacity: 1
          }}
        >
          <div 
            className="grow"
            style={{
              background: 'conic-gradient(from 180deg at 99.78% 35% in lab, rgb(255, 255, 255) 18deg, rgb(255, 208, 134) 36deg, rgba(17, 17, 17, 0) 90deg, rgba(17, 17, 17, 0) 342deg, rgb(255, 255, 255) 360deg)'
            }}
          />
          <div 
            className="grow"
            style={{
              background: 'conic-gradient(from 0deg at 99.78% 65% in lab, rgb(255, 255, 255) 0deg, rgba(17, 17, 17, 0) 18deg, rgba(17, 17, 17, 0) 270deg, rgb(255, 208, 134) 324deg, rgb(255, 255, 255) 342deg)'
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default UserSignup;
